import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import SectionIntro from "./SectionIntro";

const Overview = () => {
  const { t } = useTranslation();

  const groups = [
    {
      heading: "overview.sections.amenities",
      items: [
        "overview.amenities.smokeFree",
        "overview.amenities.airConditioning",
        "overview.amenities.internet",
        "overview.amenities.equippedKitchen",
        "overview.amenities.privateBathroom",
      ],
    },
    {
      heading: "overview.sections.services",
      items: [
        "overview.services.freeParking",
        "overview.services.breakfast",
        "overview.services.petsAllowed",
        "overview.services.sauna",
        "overview.services.terrace",
        "overview.services.garden",
      ],
    },
    {
      heading: "overview.sections.idealFor",
      items: ["overview.idealFor.families", "overview.idealFor.friendGroups"],
    },
  ];

  const Check = () => (
    <svg
      className="w-4 h-4 text-brass flex-shrink-0 mt-1"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M5 13l4 4L19 7"
      />
    </svg>
  );

  return (
    <section className="bg-cream pt-32 md:pt-40 pb-24">
      <div className="max-w-6xl mx-auto px-5 md:px-10">
        <SectionIntro
          eyebrow={t("labels.theVilla")}
          title={t("overview.title")}
          className="mb-14 md:mb-16"
        />

        {/* Feature image */}
        <motion.div
          className="overflow-hidden mb-20 md:mb-24 aspect-[16/9] bg-cream-dark"
          initial={{ opacity: 0, scale: 1.04 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        >
          <img
            src="/images/gallery/exterior5.jpg"
            alt="Laurier Rose villa"
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
        </motion.div>

        {/* Amenity groups */}
        <div className="grid md:grid-cols-2 gap-14 md:gap-16">
          {groups.map((group, gi) => (
            <motion.div
              key={gi}
              className={gi === 2 ? "md:col-span-2 max-w-md" : ""}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: gi * 0.1 }}
            >
              <h3 className="font-serif font-light text-2xl md:text-3xl text-ink">
                {t(group.heading)}
              </h3>
              <div className="rule-gold my-5" />
              <ul className="space-y-4">
                {group.items.map((item, ii) => (
                  <li key={ii} className="flex items-start gap-3">
                    <Check />
                    <span className="text-ink-soft font-light">{t(item)}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          className="text-center mt-20 md:mt-24"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Link
            to="/booking"
            className="inline-flex items-center justify-center bg-ink hover:bg-brass text-cream text-[12px] uppercase tracking-[0.22em] py-4 px-12 transition-colors duration-300"
          >
            {t("overview.bookNow")}
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default Overview;
